import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ShieldAlert } from "lucide-react";

function useProjection(batchId) {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get(`/opening-balance/batch/${batchId}/projection`)
      .then((r) => setP(r.data)).catch(() => toast.error("Failed to compute projection"));
  }, [batchId]);
  return p;
}

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white border border-slate-200">
    <div className="px-5 py-3.5 border-b border-slate-100">
      <div className="text-sm font-bold text-slate-900">{title}</div>
      {subtitle && <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const ObcBanner = ({ p }) => (
  <div className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border mb-4 ${
    p.obc_ok ? "text-emerald-700 bg-emerald-50 border-emerald-200"
             : "text-rose-700 bg-rose-50 border-rose-200"}`} data-testid="ob-obc-banner">
    {p.obc_ok ? <CheckCircle2 size={16} /> : <ShieldAlert size={16} />}
    Opening Balance Control residual: {formatINR(p.obc_residual)} —
    {p.obc_ok ? " balanced (Dr = Cr). Posting gate OPEN."
              : " the opening TB must balance to ZERO before posting (check Other Balances — equity/loans usually complete the picture)."}
  </div>
);

export const ObProjectionTB = ({ batch }) => {
  const p = useProjection(batch.id);
  if (!p) return <Card title="Step 10 — Projected Trial Balance"><div className="text-sm text-slate-500">Computing…</div></Card>;
  return (
    <Card title="Step 10 — Projected Trial Balance"
      subtitle="Dry run — the opening entries exactly as they would post at the cutoff. Nothing is written to the books.">
      <ObcBanner p={p} />
      <div className="overflow-x-auto border border-slate-100" data-testid="ob-tb-table">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#13294B]">
              {["Account Type", "Account", "Debit", "Credit"].map((h, i) => (
                <th key={h} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-300 ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {p.tb_rows.map((r, i) => (
              <tr key={i} className={`border-t border-slate-100 ${i % 2 ? "bg-slate-50/50" : ""}`}>
                <td className="px-3 py-1.5 text-slate-500 uppercase text-[10px] font-bold">{r.account_type}</td>
                <td className="px-3 py-1.5 font-semibold text-slate-800">{r.account_name}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.debit ? formatINR(r.debit) : "—"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.credit ? formatINR(r.credit) : "—"}</td>
              </tr>
            ))}
            {!p.tb_rows.length && <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">Nothing imported yet</td></tr>}
            <tr className="bg-[#0B1F3A]">
              <td colSpan={2} className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-slate-300">Total</td>
              <td className="px-3 py-2 text-right tabular-nums font-black text-orange-400" data-testid="ob-tb-total-dr">{formatINR(p.total_debit)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-black text-orange-400" data-testid="ob-tb-total-cr">{formatINR(p.total_credit)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const SECTION_LABELS = [
  ["equity", "Shareholders' Funds"],
  ["non_current_liabilities", "Non-Current Liabilities"],
  ["current_liabilities", "Current Liabilities"],
  ["non_current_assets", "Non-Current Assets"],
  ["current_assets", "Current Assets"],
];

export const ObProjectionBS = ({ batch }) => {
  const p = useProjection(batch.id);
  if (!p) return <Card title="Step 11 — Projected Balance Sheet"><div className="text-sm text-slate-500">Computing…</div></Card>;
  const t = p.bs.totals;
  return (
    <Card title="Step 11 — Projected Balance Sheet"
      subtitle="Schedule III view of the opening entries alone (as they will appear inside the statutory Balance Sheet after posting).">
      <ObcBanner p={p} />
      <div className={`mb-4 px-4 py-2.5 text-xs font-bold border ${Math.abs(t.difference) < 0.01
        ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-rose-700 bg-rose-50 border-rose-200"}`}
        data-testid="ob-bs-equation">
        Assets {formatINR(t.assets)} {Math.abs(t.difference) < 0.01 ? "=" : "≠"} Equity + Liabilities {formatINR(t.equity_and_liabilities)}
        {Math.abs(t.difference) >= 0.01 && <> · difference {formatINR(t.difference)}</>}
      </div>
      <div className="grid md:grid-cols-2 gap-4" data-testid="ob-bs-sections">
        {SECTION_LABELS.map(([key, label]) => (
          <div key={key} className="border border-slate-100">
            <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
              <span className="text-xs font-black tabular-nums text-slate-800">{formatINR(t[key] || 0)}</span>
            </div>
            {(p.bs.sections[key] || []).map((r, i) => (
              <div key={i} className="px-3 py-1.5 border-t border-slate-50 flex items-center justify-between text-[11px]">
                <span className="text-slate-700">{r.account_name} <span className="text-slate-400">({r.group})</span></span>
                <span className="tabular-nums font-semibold">{formatINR(r.amount)}</span>
              </div>
            ))}
            {!(p.bs.sections[key] || []).length && <div className="px-3 py-2 text-[11px] text-slate-400">—</div>}
          </div>
        ))}
      </div>
    </Card>
  );
};

export const ObProjectionPL = ({ batch }) => {
  const p = useProjection(batch.id);
  if (!p) return <Card title="Step 12 — Projected P&L"><div className="text-sm text-slate-500">Computing…</div></Card>;
  const ok = p.pnl.accounts_touched === 0;
  return (
    <Card title="Step 12 — Projected Profit & Loss"
      subtitle="Opening balances must never touch the P&L — this step proves it.">
      <div className={`flex items-start gap-3 px-4 py-4 border text-sm ${ok
        ? "text-emerald-800 bg-emerald-50 border-emerald-200" : "text-rose-800 bg-rose-50 border-rose-200"}`}
        data-testid="ob-pnl-proof">
        {ok ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <ShieldAlert size={18} className="mt-0.5 shrink-0" />}
        <div>
          <div className="font-bold">P&L impact: {formatINR(p.pnl.impact)} · income/expense accounts touched: {p.pnl.accounts_touched}</div>
          <p className="text-xs mt-1">{p.pnl.proof}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 max-w-2xl text-xs" data-testid="ob-category-totals">
        {Object.entries(p.category_totals || {}).map(([cat, c]) => (
          <div key={cat} className="border border-slate-100 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{cat}</div>
            <div className="font-black tabular-nums text-slate-800">{formatINR(c.total_amount || 0)}</div>
            <div className="text-[10px] text-slate-500">{c.rows_valid} valid · {c.rows_error} error(s)</div>
          </div>
        ))}
      </div>
    </Card>
  );
};
